import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import AdminNav from '../../components/admin/AdminNav';
import { translateKnown } from '../../i18n/knownMessages';
import { animalLabel } from '../../utils/animals';
import { stateLabel } from '../../utils/usStates';
import { GroupContext } from '../../context/GroupContext';

const EPSILON = 0.001;

export default function ViewGroup() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { groups, fetchGroup } = useContext(GroupContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [group, setGroup] = useState(null);
  const [showMembers, setShowMembers] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function ensureGroupLoaded() {
      setLoading(true);
      setError('');
      try {
        let foundGroup = groups.find((g) => g._id === id);

        if (!foundGroup && fetchGroup) {
          foundGroup = await fetchGroup(id);
        }

        if (!foundGroup && !cancelled) {
          setError('Group not found');
          setGroup(null);
        } else if (!cancelled) {
          setGroup(foundGroup);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Failed to load group details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    ensureGroupLoaded();

    return () => {
      cancelled = true;
    };
  }, [id, groups, fetchGroup]);

  useEffect(() => {
    const updatedGroup = groups.find((g) => g._id === id);
    if (updatedGroup) {
      setGroup(updatedGroup);
    }
  }, [groups, id]);

  if (loading) return <p className="text-center mt-6">{t('viewGroup.loading')}</p>;
  if (error) return <p className="text-red-600 text-center mt-6">{translateKnown(t, error)}</p>;
  if (!group) return <p className="text-center mt-6">{t('viewGroup.notFound')}</p>;

  const remainingCost = Number(group.remainingCost ?? group.totalCost);
  const effectiveStatus =
    group.status === 'closed'
      ? 'closed'
      : remainingCost <= EPSILON
      ? 'full'
      : 'open';

  const totalCost = parseFloat(group.totalCost) || 0;
  const totalPortions = parseInt(group.totalPortions ?? 0, 10);
  const portionsTaken = parseInt(group.portionsTaken ?? 0, 10);
  const costPerPortion = totalCost && totalPortions ? totalCost / totalPortions : 0;

  const isClosedOrFull = effectiveStatus === 'full' || effectiveStatus === 'closed';
  const remainingPortions = isClosedOrFull ? 0 : totalPortions - portionsTaken;
  const computedRemainingCost = isClosedOrFull ? 0 : remainingCost;

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-lg mx-auto mt-10 p-6 bg-white shadow-md rounded">
        <h1 className="text-3xl font-bold mb-6 text-center text-gray-800">
          {t('viewGroup.title')}
        </h1>

        <div className="space-y-3 text-gray-700">
          <Detail label={t('viewGroup.name')} value={group.name} />
          {group.shortDescription && <Detail label={t('createGroup.shortDescription')} value={group.shortDescription} />}
          <Detail label={t('createGroup.supplier')} value={group.supplierId?.displayName || group.supplierId?.name || t('common.na')} />
          <Detail label={t('createGroup.state')} value={stateLabel(group.state)} />
          <Detail label={t('adminGroups.visibility')} value={t('adminGroups.' + (group.visibility === 'private' ? 'private' : 'public'))} />
          <Detail label={t('createGroup.animal')} value={animalLabel(t, group.animal)} />

          {/* Collapsible Members */}
          <div className="border-b pb-2">
            <div
              className="flex justify-between items-center cursor-pointer select-none"
              onClick={() => setShowMembers((prev) => !prev)}
            >
              <span className="font-medium">
                {group.members?.length ? t('viewGroup.membersCount', { count: group.members.length }) : t('viewGroup.members')}
              </span>
              <span className="text-gray-500">{showMembers ? '▲' : '▼'}</span>
            </div>

            {showMembers && (
              <ul
                className="mt-2 list-disc pl-5 border rounded p-2"
                style={{
                  maxHeight: group.members?.length > 3 ? 150 : 'auto',
                  overflowY: group.members?.length > 3 ? 'auto' : 'visible',
                  minHeight: 30,
                }}
              >
                {group.members?.length ? (
                  group.members.map((m, idx) => (
                    <li key={idx} className="break-words [overflow-wrap:anywhere]">{m.email}</li>
                  ))
                ) : (
                  <li>{t('viewGroup.noMembers')}</li>
                )}
              </ul>
            )}
          </div>

          <Detail
            label={t('viewGroup.totalCost')}
            value={
              totalCost
                ? `$${totalCost.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : 'N/A'
            }
          />
          <Detail label={t('viewGroup.totalPortions')} value={group.totalPortions ?? t('common.na')} />
          <Detail
            label={t('viewGroup.portionsAvailable')}
            value={group.portionsAvailable?.length ? group.portionsAvailable.map((p) => t('portions.' + p.toLowerCase(), { defaultValue: p })).join(', ') : t('common.na')}
          />
          <Detail label={t('viewGroup.remainingCost')} value={`$${computedRemainingCost.toFixed(2)}`} />
          <Detail label={t('viewGroup.portionsRemaining')} value={isNaN(remainingPortions) ? t('common.na') : remainingPortions} />
          <Detail label={t('viewGroup.costPerPortion')} value={`$${costPerPortion.toFixed(2)}`} />
          <Detail label={t('viewGroup.effectiveStatus')} value={t('adminGroups.' + effectiveStatus)} />
          <Detail label={t('viewGroup.storedStatus')} value={t('adminGroups.' + group.status, { defaultValue: group.status })} />
          <Detail
            label={t('viewGroup.createdAt')}
            value={group.createdAt ? new Date(group.createdAt).toLocaleString() : '—'}
          />
          <Detail
            label={t('viewGroup.updatedAt')}
            value={group.updatedAt ? new Date(group.updatedAt).toLocaleString() : '—'}
          />
          <Detail
            label={t('viewGroup.dateClosed')}
            value={group.dateClosed ? new Date(group.dateClosed).toLocaleString() : t('viewGroup.notClosed')}
          />
        </div>

        <div className="flex justify-center mt-6 space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            {t('viewGroup.back')}
          </button>
          <button
            onClick={() => navigate(`/admin/groups/edit/${group._id}`)}
            disabled={!(group.myPermissions || []).includes('settings')}
            className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-yellow-500"
          >
            {t('viewGroup.editGroup')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800 px-2 py-1 rounded';
      case 'full':
        return 'bg-yellow-100 text-yellow-800 px-2 py-1 rounded';
      case 'closed':
        return 'bg-gray-200 text-gray-800 px-2 py-1 rounded';
      default:
        return '';
    }
  };
  const isStatus = label.toLowerCase().includes('status');
  return (
    <div className="flex justify-between gap-4 border-b pb-2 items-start">
      <span className="font-medium shrink-0">{label}:</span>
      <span className={`min-w-0 text-right break-words [overflow-wrap:anywhere] ${isStatus ? getStatusColor(value) : ''}`}>{value}</span>
    </div>
  );
}
